import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMobile, useScroll } from '@shared/hooks';
import { type AccordionPT } from '@shared/ui/layout/accordion/ui.tsx';
import { TextButton } from '../buttons';
import { Accordion } from '../layout';
import st from './styles.module.scss';

const preventScrollDefault = (e: WheelEvent) => {
  e.preventDefault();
};

interface Props {
  onResetFilter: () => void;
  showResetBtn: boolean;
  isOpen: boolean;
  sections: AccordionPT[];
  classNameOnOpen?: string;
}

/**
 * Компонент бокового фильтра.
 *
 * @param {AccordionPT[]}  sections - массив фильтров
 * @param {string} sections[].title - название раздела фильтров
 * @param {React.ReactNode} sections[].children - список фильтров внутри раздела
 * @param {string} classNameOnOpen - тут без слов понятно, надеюсь
 */
export const AsideFilter = ({
  onResetFilter,
  showResetBtn,
  sections,
  isOpen,
  classNameOnOpen,
}: Props) => {
  const { hideHeader } = useScroll();
  const [transformY, setTransformY] = useState(0);
  const isTransformUp = useMemo(() => hideHeader, [hideHeader]);
  const filterRef = useRef<HTMLElement>(null);
  const { isMobile } = useMobile();

  const onFilterStateChange = useCallback(() => {
    setTimeout(() => {
      const el = filterRef?.current;
      if (el) {
        if (el.clientHeight >= window.innerHeight - 114) {
          el.removeEventListener('wheel', preventScrollDefault);
        } else {
          el.addEventListener('wheel', preventScrollDefault);
        }
      }
    }, 0);
  }, [filterRef]);

  useEffect(() => {
    if (!isTransformUp) {
      setTransformY(0);
    } else {
      setTransformY(-45);
    }
  }, [isTransformUp]);

  useEffect(() => {
    if (filterRef) {
      onFilterStateChange();
    }
  }, [filterRef, onFilterStateChange]);

  return (
    <div className={cn(st.filterWrapper, isOpen ? cn(st.open, classNameOnOpen) : st.close)}>
      <div className={st.stickyContainer} style={{ transform: `translateY(${transformY}px)` }}>
        <aside
          ref={filterRef}
          className={`${st.filter} ${isOpen ? cn(st.open, classNameOnOpen) : ''}`}
          onClick={onFilterStateChange}
        >
          {showResetBtn && (
            <div className={st.filter__resetBtn}>
              <TextButton size="small" variant="dark" onClick={onResetFilter}>
                Сбросить
              </TextButton>
            </div>
          )}
          {sections.map((section, i) => (
            <Accordion
              {...section}
              key={section.title}
              titleClassName={st.title}
              bodyClassNameOnOpen={st.accordionBodyOnOpenClassName}
              closeArrowClassName={st.closeArrowClassName}
              className={cn(
                section?.className,
                i === sections.length - 1 && isMobile ? st.lastSectionMobile : null
              )}
            />
          ))}
        </aside>
      </div>
    </div>
  );
};
